"use client";

import { useMemo, type ComponentType, type ReactNode } from "react";
import { AppLink as Link } from "@/components/AppLink";
import { useAuth } from "@/context/AuthContext";
import { useSolicitudes } from "@/hooks/useSolicitudes";
import { isPreClasico } from "@/lib/preClasicos";
import {
  mapEstadoKanban,
  getCardPaymentDeadlineInfo,
  getReclamacionDocsDeadlineInfo,
} from "@/lib/solicitudHelpers";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { pageEnter } from "@/lib/motion-presets";
import { Receipt, XCircle, ChevronRight } from "lucide-react";
import {
  AlertVintageIcon,
  DocumentOkVintageIcon,
  CarClassicIcon,
  DocumentVintageIcon,
  DocumentErrorVintageIcon,
  PocketClockIcon,
  CalendarVintageIcon,
  ArchiveVintageIcon,
  ShieldVintageIcon,
} from "@/components/ui/segurclassic-icons";

type Cola = "entrada" | "listas" | "emision" | "cobro" | "errores" | "snooze";
type Tone = "default" | "danger" | "warning";
type MenuIcon = ComponentType<{ size?: number; className?: string }>;

const COLAS: {
  id: Cola;
  label: string;
  tab: string;
  description: string;
  icon: MenuIcon;
}[] = [
  {
    id: "entrada",
    label: "Entrada",
    tab: "entrada",
    description: "Revisar las nuevas y pedir la documentación que falta.",
    icon: AlertVintageIcon,
  },
  {
    id: "listas",
    label: "Listas para emitir",
    tab: "listas",
    description: "Aprobar y lanzar las que ya tienen la documentación completa.",
    icon: DocumentOkVintageIcon,
  },
  {
    id: "emision",
    label: "Emisión en curso",
    tab: "emision",
    description: "Seguir la cola automática y terminar las de emisión manual.",
    icon: CarClassicIcon,
  },
  {
    id: "cobro",
    label: "Cobro",
    tab: "pre_emision",
    description: "Confirmar el cobro y enviar la póliza al cliente.",
    icon: DocumentVintageIcon,
  },
  {
    id: "errores",
    label: "Errores",
    tab: "errores",
    description: "Corregir las que no se han podido emitir.",
    icon: DocumentErrorVintageIcon,
  },
  {
    id: "snooze",
    label: "En espera",
    tab: "snooze",
    description: "Retomar las que dejaste aparcadas.",
    icon: PocketClockIcon,
  },
];

const OTRAS: {
  href: string;
  label: string;
  description: string;
  icon: MenuIcon;
}[] = [
  {
    href: "/calendario",
    label: "Calendario",
    description: "Fechas de efecto",
    icon: CalendarVintageIcon,
  },
  {
    href: "/emision/control-recibos",
    label: "Recibos",
    description: "Pólizas emitidas",
    icon: Receipt,
  },
  {
    href: "/archivo",
    label: "Archivo",
    description: "Histórico",
    icon: ArchiveVintageIcon,
  },
  {
    href: "/scoring",
    label: "Scoring",
    description: "DNI y matrículas",
    icon: ShieldVintageIcon,
  },
  {
    href: "/canceladas",
    label: "Canceladas",
    description: "Reabrir si hace falta",
    icon: XCircle,
  },
];

function getSaludo() {
  const h = new Date().getHours();
  if (h < 7 || h >= 20) return "Buenas noches";
  if (h < 13) return "Buenos días";
  return "Buenas tardes";
}

function colaDe(estado: string): Cola | null {
  if (estado === "NUEVA_SOLICITUD" || estado === "PENDIENTE_DOCUMENTACION") return "entrada";
  if (estado === "DOCUMENTACION_COMPLETA") return "listas";
  if (estado === "EN_EMISION_AUTOMATICA" || estado === "EMISION_MANUAL") return "emision";
  if (estado === "PRE_EMISION" || estado === "POLIZA_LISTA" || estado === "PENDIENTE_COBRO") return "cobro";
  if (estado === "ERROR_EMISION") return "errores";
  if (estado === "SNOOZE") return "snooze";
  return null;
}

function emptyCounts(): Record<Cola, number> {
  return { entrada: 0, listas: 0, emision: 0, cobro: 0, errores: 0, snooze: 0 };
}

export default function HomePage() {
  const { usuarioRPA } = useAuth();
  const { solicitudes, loading } = useSolicitudes();
  const nombre = usuarioRPA?.nombre?.split(" ")[0] || "";

  const resumen = useMemo(() => {
    const clasicos = emptyCounts();
    const pre = emptyCounts();
    let cobrosTarjetaUrgentes = 0;
    let reclamacionesPre = 0;

    for (const s of solicitudes) {
      const cola = colaDe(mapEstadoKanban(s));
      const destino = isPreClasico(s) ? pre : clasicos;
      if (cola) destino[cola] += 1;

      if (!isPreClasico(s)) {
        const pago = getCardPaymentDeadlineInfo(s);
        if (pago && (pago.urgente || pago.vencido)) cobrosTarjetaUrgentes += 1;
      } else if (cola === "entrada") {
        const rec = getReclamacionDocsDeadlineInfo(s);
        if (rec && !rec.pausada && (rec.vencido || rec.diasRestantes <= 3)) reclamacionesPre += 1;
      }
    }

    return { clasicos, pre, cobrosTarjetaUrgentes, reclamacionesPre };
  }, [solicitudes]);

  return (
    <motion.div className="space-y-6" {...pageEnter}>
      <header className="relative overflow-hidden rounded-2xl bg-segurclassic-green text-white shadow-md">
        <div className="absolute inset-x-0 top-0 h-1 bg-segurclassic-gold" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="pointer-events-none absolute -right-1 bottom-0 hidden opacity-25 sm:block">
          <CarClassicIcon size={128} primaryColor="#DA9200" secondaryColor="#F3F4F5" />
        </div>
        <div className="relative px-6 py-7 md:px-8 md:py-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-segurclassic-gold">
            Mesa de trabajo
          </p>
          <h1
            className="mt-2 font-serif text-4xl tracking-tight text-white md:text-5xl"
            suppressHydrationWarning
          >
            {getSaludo()}{nombre ? `, ${nombre}` : ""}
          </h1>
          <p className="mt-3 max-w-lg text-sm text-white/75">
            Elige la cola y entra directo.
          </p>
        </div>
      </header>

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Carpeta
          title="Clásicos"
          kicker="30 años o más"
          rule="green"
        >
          {COLAS.map((cola) => (
            <Fila
              key={cola.id}
              href={`/emision?tab=${cola.tab}`}
              label={cola.label}
              description={cola.description}
              icon={cola.icon}
              count={loading ? null : resumen.clasicos[cola.id]}
              rule="green"
              tone={
                cola.id === "errores" && resumen.clasicos.errores > 0
                  ? "danger"
                  : cola.id === "cobro" && resumen.cobrosTarjetaUrgentes > 0
                    ? "warning"
                    : "default"
              }
              note={
                cola.id === "cobro" && resumen.cobrosTarjetaUrgentes > 0
                  ? `${resumen.cobrosTarjetaUrgentes} cobro${resumen.cobrosTarjetaUrgentes === 1 ? "" : "s"} con tarjeta a punto de caducar`
                  : undefined
              }
            />
          ))}
        </Carpeta>

        <Carpeta
          title="Pre-clásicos"
          kicker="20 a 29 años"
          rule="gold"
        >
          {COLAS.map((cola) => (
            <Fila
              key={cola.id}
              href={`/pre-clasicos?tab=${cola.tab}`}
              label={cola.label}
              description={cola.description}
              icon={cola.icon}
              count={loading ? null : resumen.pre[cola.id]}
              rule="gold"
              tone={
                cola.id === "errores" && resumen.pre.errores > 0
                  ? "danger"
                  : cola.id === "entrada" && resumen.reclamacionesPre > 0
                    ? "warning"
                    : "default"
              }
              note={
                cola.id === "entrada" && resumen.reclamacionesPre > 0
                  ? resumen.reclamacionesPre === 1
                    ? "1 reclamación de documentación vence pronto o ya venció"
                    : `${resumen.reclamacionesPre} reclamaciones de documentación vencen pronto o ya vencieron`
                  : undefined
              }
            />
          ))}
        </Carpeta>
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-5 py-3 dark:border-slate-800">
          <h2 className="font-serif text-xl text-segurclassic-green dark:text-segurclassic-gold">
            Consultar
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-1 p-2 sm:grid-cols-3 lg:grid-cols-5">
          {OTRAS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="group flex items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-segurclassic-green/[0.05] dark:hover:bg-white/[0.04]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-segurclassic-green/8 text-segurclassic-green dark:bg-segurclassic-gold/10 dark:text-segurclassic-gold">
                <item.icon size={18} className="h-[18px] w-[18px]" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold leading-tight">{item.label}</span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">{item.description}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

function Carpeta({
  title,
  kicker,
  rule,
  children,
}: {
  title: string;
  kicker: string;
  rule: "green" | "gold";
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border bg-white shadow-sm dark:bg-slate-900",
        rule === "green"
          ? "border-segurclassic-green/15 dark:border-slate-800"
          : "border-segurclassic-gold/35 dark:border-segurclassic-gold/25",
      )}
    >
      <div className={cn("h-1", rule === "green" ? "bg-segurclassic-green" : "bg-segurclassic-gold")} />
      <div className="flex items-baseline justify-between gap-3 px-5 pb-1 pt-4">
        <h2 className="font-serif text-2xl tracking-tight text-segurclassic-green dark:text-segurclassic-gold">
          {title}
        </h2>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {kicker}
        </p>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">{children}</div>
    </section>
  );
}

function Fila({
  href,
  label,
  description,
  icon: Icon,
  count,
  rule,
  tone = "default",
  note,
}: {
  href: string;
  label: string;
  description: string;
  icon: MenuIcon;
  count: number | null;
  rule: "green" | "gold";
  tone?: Tone;
  note?: string;
}) {
  const waiting = typeof count === "number" && count > 0;
  return (
    <Link
      href={href}
      className={cn(
        "group flex items-center gap-3 px-4 py-3.5 transition-colors sm:px-5",
        tone === "danger" && "bg-red-50/80 hover:bg-red-50 dark:bg-red-950/20 dark:hover:bg-red-950/30",
        tone === "warning" && "bg-amber-50/80 hover:bg-amber-50 dark:bg-amber-950/20 dark:hover:bg-amber-950/30",
        tone === "default" && "hover:bg-segurclassic-green/[0.04] dark:hover:bg-white/[0.03]",
      )}
    >
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
          tone === "danger" && "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
          tone === "warning" && "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
          tone === "default" && rule === "green" && "bg-segurclassic-green/8 text-segurclassic-green dark:bg-white/5 dark:text-segurclassic-gold",
          tone === "default" && rule === "gold" && "bg-segurclassic-gold/12 text-segurclassic-green dark:bg-segurclassic-gold/10 dark:text-segurclassic-gold",
        )}
      >
        <Icon size={18} className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium leading-tight">{label}</span>
        <span className="mt-0.5 block text-sm text-muted-foreground">{description}</span>
        {note && (
          <span
            className={cn(
              "mt-1 block text-xs font-medium",
              tone === "danger" ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-400",
            )}
          >
            {note}
          </span>
        )}
      </span>
      {count === null ? (
        <span className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-slate-100 dark:bg-slate-800" />
      ) : waiting ? (
        <span
          className={cn(
            "flex h-8 min-w-8 shrink-0 items-center justify-center rounded-full px-2 font-serif text-sm tabular-nums",
            tone === "danger" && "bg-red-600 text-white",
            tone === "warning" && "bg-amber-500 text-segurclassic-green",
            tone === "default" && "bg-segurclassic-green text-segurclassic-gold",
          )}
          aria-label={`${count} en espera`}
        >
          {count}
        </span>
      ) : null}
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-segurclassic-green dark:text-slate-600 dark:group-hover:text-segurclassic-gold" />
    </Link>
  );
}
